import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { eur, F, Num, Sel, Chk, Row, Big, Form, n, CLASSES, useForm, inp } from "./Calculators";

const fmtD = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : "—");
const ROM = ["", "I", "II", "III", "IV", "V", "VI", "VII"];

function TownSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2 || picked?.name === q) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/tools/mietstufe", { params: { q } }).then((r) => setRes(r.data.results)).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q, picked]);
  const pick = (t) => { setPicked(t); setQ(t.name); setRes([]); onPick(t); };
  return (
    <div className="relative" data-testid="town-search">
      <input value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} placeholder="Upiši grad ili općinu (npr. Offenbach)" className={inp} aria-label="Grad ili općina" data-testid="in-town" />
      {res.length > 0 && <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-sm border border-border bg-card shadow-lg" data-testid="town-results">
        {res.map((t) => <li key={`${t.state}-${t.name}-${t.plz}`}><button type="button" onClick={() => pick(t)} className="w-full px-3 py-2 text-left text-sm hover:bg-surface" data-testid="town-option">{t.name} <span className="text-muted-foreground">· {t.kreis} · {t.state}{t.plz ? ` · ${t.plz}` : ""}</span> <b className="float-right">{ROM[t.mietstufe]}</b></button></li>)}
      </ul>}
      {picked && <p className="mt-1 text-xs text-muted-foreground" data-testid="town-picked">{picked.name}: Mietstufe <b>{ROM[picked.mietstufe]}</b> {picked.via === "kreis" ? `(općina nije posebno navedena – važi Mietstufe kreisa ${picked.kreis})` : "(navedena u službenoj listi)"}</p>}
    </div>
  );
}

function Minijob({ onRun, busy, states }) {
  const [f, set] = useForm({ gross: "603", gross_b: "", weekly_hours: "10", tax_class: "1", state: "NW", children: "0", age: "30" });
  const submit = () => onRun({ gross: n(f.gross), gross_b: f.gross_b ? n(f.gross_b) : null, weekly_hours: n(f.weekly_hours), tax_class: +f.tax_class, state: f.state, children: +f.children, age: +f.age });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Bruto mjesečno (€)"><Num id="gross" v={f.gross} set={set("gross")} required /></F>
        <F label="Sedmični sati"><Num id="hours" v={f.weekly_hours} set={set("weekly_hours")} /></F>
        <F label="Poređenje: druga bruto plata (€)" hint="Opcionalno"><Num id="gross-b" v={f.gross_b} set={set("gross_b")} /></F>
        <F label="Porezna klasa"><Sel id="tax-class" v={f.tax_class} set={set("tax_class")} opts={CLASSES} /></F>
        <F label="Savezna pokrajina"><Sel id="state" v={f.state} set={set("state")} opts={Object.entries(states)} /></F>
        <F label="Broj djece"><Num id="children" v={f.children} set={set("children")} /></F>
        <F label="Godine"><Num id="age" v={f.age} set={set("age")} /></F>
      </div>
    </Form>
  );
}
const MODE = { minijob: "Minijob", midijob: "Midijob (prelazno područje)", normal: "Redovno zaposlenje" };
const MiniCol = ({ r, id }) => (
  <div data-testid={id}>
    <Row l="Vrsta" v={MODE[r.mode]} strong />
    <Row l="Bruto" v={eur(r.gross)} /><Row l="Doprinosi radnika" v={`− ${eur(r.employee_sv)}`} /><Row l="Porez" v={`− ${eur(r.tax)}`} />
    <Row l="Neto" v={eur(r.net)} strong /><Row l="Satnica" v={eur(r.hourly)} />
    {r.employer_cost != null && <Row l="Trošak poslodavca" v={eur(r.employer_cost)} />}
  </div>
);
Minijob.Result = ({ r }) => (
  <div data-testid="result-minijob">
    <Big l={`Neto mjesečno – ${MODE[r.a.mode]}`} v={eur(r.a.net)} id="res-mini-net" />
    <div className={r.b ? "grid gap-4 sm:grid-cols-2" : ""}><MiniCol r={r.a} id="res-mini-a" />{r.b && <MiniCol r={r.b} id="res-mini-b" />}</div>
    <Row l="Granica Minijoba / Midijoba" v={`${eur(r.minijob_limit)} / ${eur(r.midijob_limit)}`} />
    <Row l="Najviše sati u Minijobu uz minimalnu satnicu" v={`${r.max_hours_minijob_month} h/mj. (~${r.max_hours_minijob_week} h/sedm.)`} id="res-mini-hours" />
  </div>
);

function Alg({ onRun, busy, states }) {
  const [f, set] = useForm({ gross_year: "36000", tax_class: "1", child: false, age: "40", months_30: "24", months_5y: "24", state: "NW" });
  const submit = () => onRun({ ...f, gross_year: n(f.gross_year), tax_class: +f.tax_class, age: +f.age, months_30: +f.months_30, months_5y: +f.months_5y });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Bruto zarada u zadnjih 12 mjeseci (€)"><Num id="gross-year" v={f.gross_year} set={set("gross_year")} required /></F>
        <F label="Porezna klasa"><Sel id="tax-class" v={f.tax_class} set={set("tax_class")} opts={CLASSES} /></F>
        <F label="Godine starosti"><Num id="age" v={f.age} set={set("age")} /></F>
        <F label="Savezna pokrajina"><Sel id="state" v={f.state} set={set("state")} opts={Object.entries(states)} /></F>
        <F label="Mjeseci osiguranja u zadnjih 30 mjeseci"><Num id="m30" v={f.months_30} set={set("months_30")} /></F>
        <F label="Mjeseci osiguranja u zadnjih 5 godina" hint="Za trajanje"><Num id="m5" v={f.months_5y} set={set("months_5y")} /></F>
      </div>
      <Chk id="child" v={f.child} set={set("child")} label="Imam dijete (pravo na Kindergeld / Kinderfreibetrag)" />
    </Form>
  );
}
Alg.Result = ({ r }) => !r.eligible ? <p className="rounded-sm bg-amber-50 p-4 text-sm" data-testid="result-alg">Po unosima nema prava na ALG I.</p> : (
  <div data-testid="result-alg">
    <Big l="ALG I mjesečno (procjena)" v={eur(r.monthly)} id="res-alg-month" />
    <Row l="Dnevno" v={eur(r.daily)} /><Row l="Stopa" v={`${r.rate} %`} />
    <Row l="Bemessungsentgelt dnevno" v={eur(r.bemessung_daily)} /><Row l="Leistungsentgelt dnevno" v={eur(r.leistung_daily)} />
    <Row l="Trajanje" v={`${r.duration_months} mjeseci`} strong id="res-alg-duration" />
  </div>
);

function Leave({ onRun, busy }) {
  const [f, set] = useForm({ days_per_week: "5", contract_days: "", start: "2024-01-01", year: "2026", end: "", notice_date: "", side: "employee", probation: false });
  const submit = () => onRun({ ...f, days_per_week: n(f.days_per_week), contract_days: f.contract_days ? n(f.contract_days) : null, year: +f.year, end: f.end || null, notice_date: f.notice_date || null });
  const D = ({ k, id }) => <input type="date" value={f[k]} onChange={(e) => set(k)(e.target.value)} className={inp} data-testid={`in-${id}`} />;
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Radnih dana u sedmici"><Num id="dpw" v={f.days_per_week} set={set("days_per_week")} /></F>
        <F label="Dana odmora po ugovoru" hint="Opcionalno"><Num id="contract-days" v={f.contract_days} set={set("contract_days")} /></F>
        <F label="Početak radnog odnosa"><D k="start" id="start" /></F>
        <F label="Godina"><Num id="year" v={f.year} set={set("year")} /></F>
        <F label="Kraj radnog odnosa" hint="Ako je poznat"><D k="end" id="end" /></F>
        <F label="Datum prijema otkaza" hint="Za otkazni rok"><D k="notice_date" id="notice" /></F>
        <F label="Ko daje otkaz"><Sel id="side" v={f.side} set={set("side")} opts={[["employee", "Radnik"], ["employer", "Poslodavac"]]} /></F>
      </div>
      <Chk id="probation" v={f.probation} set={set("probation")} label="Još sam u probnom radu (najviše 6 mjeseci)" />
    </Form>
  );
}
Leave.Result = ({ r }) => (
  <div data-testid="result-leave">
    <Big l="Zakonski odmor u godini" v={`${r.statutory_year} dana`} id="res-leave-days" />
    <Row l="Zakonski minimum za punu godinu" v={`${r.statutory_full} dana`} /><Row l="Pravo za punu godinu (s ugovorom)" v={`${r.entitled_full} dana`} />
    {r.partial && <Row l="Puni mjeseci (djelimično pravo)" v={r.full_months} />}
    {r.notice && <><Row l="Najraniji kraj radnog odnosa" v={fmtD(r.notice.last_day)} strong id="res-notice-end" /><p className="mt-2 text-sm">{r.notice.rule}</p></>}
  </div>
);

function Wohngeld({ onRun, busy }) {
  const [f, set] = useForm({ persons: "1", mietstufe: "3", rent: "500", income_year: "15000", earners: "1", ded_tax: true, ded_kv: true, ded_rv: true, excluded: false });
  const submit = () => onRun({ ...f, persons: +f.persons, mietstufe: +f.mietstufe, rent: n(f.rent), income_year: n(f.income_year), earners: +f.earners });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Broj osoba u domaćinstvu"><Num id="persons" v={f.persons} set={set("persons")} /></F>
        <F label="Grad / općina" hint={<>Mietstufe iz službene liste (WoGV, od 1. 1. 2023) – ili je izaberi ručno ispod. <a href="https://www.bmwsb.bund.de/SharedDocs/downloads/DE/veroeffentlichungen/wohnen/wohngeld-2023/mietstufen-2023.html" target="_blank" rel="noreferrer nofollow" className="underline" data-testid="mietstufe-link">Izvor</a></>}>
          <TownSearch onPick={(t) => set("mietstufe")(String(t.mietstufe))} /></F>
        <F label="Mietstufe">
          <Sel id="mietstufe" v={f.mietstufe} set={set("mietstufe")} opts={["I", "II", "III", "IV", "V", "VI", "VII"].map((x, i) => [String(i + 1), `Mietstufe ${x}`])} /></F>
        <F label="Bruttokaltmiete mjesečno (€)" hint="Kirija + hladni režijski, bez grijanja"><Num id="rent" v={f.rent} set={set("rent")} required /></F>
        <F label="Godišnji bruto prihod domaćinstva (€)"><Num id="income" v={f.income_year} set={set("income_year")} /></F>
        <F label="Broj zaposlenih u domaćinstvu"><Num id="earners" v={f.earners} set={set("earners")} /></F>
      </div>
      <p className="text-sm font-semibold">Odbici od prihoda (po 10 %):</p>
      <Chk id="ded-tax" v={f.ded_tax} set={set("ded_tax")} label="Plaća se porez na dohodak" />
      <Chk id="ded-kv" v={f.ded_kv} set={set("ded_kv")} label="Plaća se zdravstveno i osiguranje za njegu" />
      <Chk id="ded-rv" v={f.ded_rv} set={set("ded_rv")} label="Plaća se penziono osiguranje" />
      <Chk id="excluded" v={f.excluded} set={set("excluded")} label="Primam Bürgergeld, socijalnu pomoć ili Grundsicherung" />
    </Form>
  );
}
Wohngeld.Result = ({ r }) => (
  <div data-testid="result-wohngeld">
    <Big l={r.eligible ? "Wohngeld mjesečno (procjena)" : "Wohngeld"} v={r.eligible ? eur(r.monthly) : "nema iznosa"} id="res-wg" />
    {r.M != null && <><Row l="Priznata kirija M (s komponentama)" v={eur(r.M)} /><Row l="Prihod Y mjesečno" v={eur(r.Y)} /><Row l="Höchstbetrag" v={eur(r.hoechstbetrag)} /></>}
  </div>
);

function Kiz({ onRun, busy }) {
  const [f, set] = useForm({ single: false, children: "2", parents_gross: "2200", child_income: "0", kindergeld: true, buergergeld: false });
  const submit = () => onRun({ ...f, children: +f.children, parents_gross: n(f.parents_gross), child_income: n(f.child_income) });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Broj djece do 25 u domaćinstvu"><Num id="children" v={f.children} set={set("children")} /></F>
        <F label="Bruto prihod roditelja mjesečno (€)"><Num id="parents-gross" v={f.parents_gross} set={set("parents_gross")} /></F>
        <F label="Prihod djece mjesečno (€)" hint="Alimentacija, Unterhaltsvorschuss…"><Num id="child-income" v={f.child_income} set={set("child_income")} /></F>
      </div>
      <Chk id="single" v={f.single} set={set("single")} label="Samohrani roditelj" />
      <Chk id="kg" v={f.kindergeld} set={set("kindergeld")} label="Primam Kindergeld za djecu" />
      <Chk id="bg" v={f.buergergeld} set={set("buergergeld")} label="Primam Bürgergeld" />
    </Form>
  );
}
Kiz.Result = ({ r }) => (
  <div data-testid="result-kiz">
    <Big l={r.possible ? "Kinderzuschlag – najviše mjesečno" : "Kinderzuschlag"} v={r.possible ? eur(r.max_total) : "uslovi nisu ispunjeni"} id="res-kiz" />
    {r.checks.map((c) => <Row key={c.label} l={c.label} v={c.ok ? "✓" : "✗"} />)}
  </div>
);

const KEYS = [["flaeche", "Površina (m²)"], ["personen", "Broj osoba"], ["einheiten", "Broj stanova"], ["verbrauch", "Potrošnja"], ["direkt", "Direktno moj iznos"]];
function Nebenkosten({ onRun, busy }) {
  const [f, set] = useForm({ advance_month: "200", months: "12" });
  const [rows, setRows] = useState([{ label: "Grijanje", total: "12000", key: "flaeche", mine: "70", all: "1000" }, { label: "Voda", total: "4000", key: "personen", mine: "2", all: "25" }]);
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const submit = () => onRun({ advance_month: n(f.advance_month), months: +f.months, rows: rows.map((r) => ({ ...r, total: n(r.total), mine: n(r.mine || 0), all: n(r.all || 0) })) });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Mjesečni predujam (€)"><Num id="advance" v={f.advance_month} set={set("advance_month")} /></F>
        <F label="Mjeseci u obračunu"><Num id="months" v={f.months} set={set("months")} /></F>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 rounded-sm border border-border p-3 sm:grid-cols-2" data-testid={`nk-row-${i}`}>
          <input value={r.label} onChange={(e) => upd(i, "label", e.target.value)} className={inp} placeholder="Stavka" data-testid={`nk-label-${i}`} />
          <input value={r.total} onChange={(e) => upd(i, "total", e.target.value)} className={inp} placeholder={r.key === "direkt" ? "Moj iznos €" : "Ukupno za zgradu €"} data-testid={`nk-total-${i}`} />
          <select value={r.key} onChange={(e) => upd(i, "key", e.target.value)} className={inp} data-testid={`nk-key-${i}`}>{KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          {r.key !== "direkt" && <div className="flex gap-2"><input value={r.mine} onChange={(e) => upd(i, "mine", e.target.value)} className={inp} placeholder="Moje" data-testid={`nk-mine-${i}`} /><input value={r.all} onChange={(e) => upd(i, "all", e.target.value)} className={inp} placeholder="Ukupno" data-testid={`nk-all-${i}`} /></div>}
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`nk-del-${i}`}><Trash2 className="h-3 w-3" /> ukloni</button>
        </div>))}
      <button type="button" onClick={() => setRows([...rows, { label: "", total: "", key: "flaeche", mine: "", all: "" }])} className="inline-flex items-center gap-1 text-sm font-semibold" data-testid="nk-add"><Plus className="h-4 w-4" /> Dodaj stavku</button>
    </Form>
  );
}
Nebenkosten.Result = ({ r }) => (
  <div data-testid="result-nk">
    <Big l={r.result === "doplata" ? "Doplata" : r.result === "povrat" ? "Povrat" : "Izravnano"} v={eur(Math.abs(r.balance))} id="res-nk-balance" />
    {r.rows.map((x) => <Row key={x.label} l={`${x.label}${x.pct != null ? ` (${x.pct} %)` : ""}`} v={eur(x.share)} />)}
    <Row l="Moji troškovi ukupno" v={eur(r.costs)} strong /><Row l="Plaćeni predujmovi" v={eur(r.advances)} /><Row l="Predujam koji bi pokrio troškove" v={eur(r.suggested_advance)} />
  </div>
);

const EnergyFields = ({ f, set, p }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <F label="Potrošnja godišnje (kWh)"><Num id={`${p}-kwh`} v={f[`${p}_kwh`]} set={set(`${p}_kwh`)} /></F>
    <F label="Cijena (ct/kWh)"><Num id={`${p}-price`} v={f[`${p}_price`]} set={set(`${p}_price`)} /></F>
    <F label="Osnovna cijena godišnje (€)"><Num id={`${p}-base`} v={f[`${p}_base`]} set={set(`${p}_base`)} /></F>
    <F label="Bonus (€)"><Num id={`${p}-bonus`} v={f[`${p}_bonus`]} set={set(`${p}_bonus`)} /></F>
  </div>
);
function Energy({ onRun, busy }) {
  const [f, set] = useForm({ a_kwh: "2500", a_price: "32", a_base: "150", a_bonus: "0", b_kwh: "2500", b_price: "", b_base: "", b_bonus: "0", installment: "75" });
  const o = (p) => ({ kwh: n(f[`${p}_kwh`]), price_ct: n(f[`${p}_price`]), base_year: n(f[`${p}_base`] || 0), bonus: n(f[`${p}_bonus`] || 0) });
  const submit = () => onRun({ a: o("a"), b: f.b_price ? o("b") : null, installment: n(f.installment || 0) });
  return (
    <Form onSubmit={submit} busy={busy}>
      <p className="font-semibold">Trenutni ugovor / ponuda A</p><EnergyFields f={f} set={set} p="a" />
      <F label="Moja mjesečna rata (Abschlag, €)"><Num id="installment" v={f.installment} set={set("installment")} /></F>
      <p className="font-semibold">Ponuda B (opcionalno)</p><EnergyFields f={f} set={set} p="b" />
    </Form>
  );
}
Energy.Result = ({ r }) => (
  <div data-testid="result-energy">
    <Big l="Godišnji trošak A" v={eur(r.a.year)} id="res-energy-a" />
    <Row l="Prva godina s bonusom" v={eur(r.a.first_year)} /><Row l="Mjesečno potrebno" v={eur(r.a.monthly)} /><Row l="Cijena po kWh sa svim troškovima" v={`${r.a.per_kwh_all_in} ct`} />
    {r.balance != null && <Row l={r.balance < 0 ? "Očekivana doplata" : "Očekivani povrat"} v={eur(Math.abs(r.balance))} strong id="res-energy-balance" />}
    {r.b && <><Row l="Godišnji trošak B" v={eur(r.b.year)} id="res-energy-b" /><Row l="Jeftinije u 1. godini" v={`${r.cheaper_first ? r.cheaper_first.toUpperCase() : "isto"} (${eur(r.diff_first)})`} strong /></>}
  </div>
);

function Home({ onRun, busy, states }) {
  const [f, set] = useForm({ price: "300000", equity: "60000", state: "NW", notary_pct: "1.5", land_pct: "0.5", broker_pct: "3.57", rate: "3.5", repay: "2", fixed_years: "10" });
  const submit = () => onRun({ ...f, price: n(f.price), equity: n(f.equity), notary_pct: n(f.notary_pct), land_pct: n(f.land_pct), broker_pct: n(f.broker_pct), rate: n(f.rate), repay: n(f.repay), fixed_years: +f.fixed_years });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Kupoprodajna cijena (€)"><Num id="price" v={f.price} set={set("price")} required /></F>
        <F label="Vlastiti kapital (€)"><Num id="equity" v={f.equity} set={set("equity")} /></F>
        <F label="Savezna pokrajina"><Sel id="state" v={f.state} set={set("state")} opts={Object.entries(states)} /></F>
        <F label="Notar (% cijene)"><Num id="notary" v={f.notary_pct} set={set("notary_pct")} /></F>
        <F label="Gruntovnica (% cijene)"><Num id="land" v={f.land_pct} set={set("land_pct")} /></F>
        <F label="Posrednik – tvoj dio (%)" hint="0 ako nema posrednika"><Num id="broker" v={f.broker_pct} set={set("broker_pct")} /></F>
        <F label="Kamata (% godišnje)"><Num id="rate" v={f.rate} set={set("rate")} /></F>
        <F label="Početna otplata (% godišnje)"><Num id="repay" v={f.repay} set={set("repay")} /></F>
        <F label="Fiksna kamata (godina)"><Num id="fixed" v={f.fixed_years} set={set("fixed_years")} /></F>
      </div>
    </Form>
  );
}
Home.Result = ({ r }) => (
  <div data-testid="result-home">
    <Big l="Mjesečna rata" v={eur(r.monthly)} id="res-home-rate" />
    <Row l={`Grunderwerbsteuer (${r.grest_rate} %)`} v={eur(r.grest)} id="res-home-grest" /><Row l="Notar" v={eur(r.notary)} /><Row l="Gruntovnica" v={eur(r.land)} /><Row l="Posrednik" v={eur(r.broker)} />
    <Row l="Sporedni troškovi ukupno" v={eur(r.side_costs)} strong /><Row l="Iznos kredita" v={eur(r.loan)} />
    <Row l={`Kamata za ${r.fixed_years} god.`} v={eur(r.interest_fixed)} /><Row l={`Ostatak duga nakon ${r.fixed_years} god.`} v={eur(r.rest_after_fixed)} strong />
  </div>
);

function Lease({ onRun, busy }) {
  const [f, set] = useForm({ months: "36", lease_down: "3000", lease_monthly: "299", lease_end: "0", lease_fees: "900", price: "32000", credit_down: "3000", credit_rate: "5.9", balloon: "12000", residual_value: "18000", maintenance_lease: "40", maintenance_credit: "60" });
  const submit = () => onRun(Object.fromEntries(Object.entries(f).map(([k, v]) => [k, k === "months" ? +v : n(v || 0)])));
  const L = [["months", "Trajanje (mjeseci)"], ["lease_down", "Leasing: učešće (€)"], ["lease_monthly", "Leasing: mjesečna rata (€)"], ["lease_end", "Leasing: završna uplata / Mehrkilometer (€)"], ["lease_fees", "Leasing: naknade (prevoz, registracija) (€)"],
    ["price", "Kredit: cijena auta (€)"], ["credit_down", "Kredit: učešće (€)"], ["credit_rate", "Kredit: kamata (%)"], ["balloon", "Kredit: završna (balonska) rata (€)"], ["residual_value", "Vrijednost auta na kraju (€)"],
    ["maintenance_lease", "Održavanje mjesečno – leasing (€)"], ["maintenance_credit", "Održavanje mjesečno – kredit (€)"]];
  return <Form onSubmit={submit} busy={busy}><div className="grid gap-4 sm:grid-cols-2">{L.map(([k, l]) => <F key={k} label={l}><Num id={k} v={f[k]} set={set(k)} /></F>)}</div></Form>;
}
Lease.Result = ({ r }) => (
  <div data-testid="result-lease">
    <Big l={r.cheaper ? `Jeftinije: ${r.cheaper}` : "Jednako"} v={eur(r.difference)} id="res-lease-diff" />
    <Row l="Leasing ukupno" v={eur(r.lease_total)} strong /><Row l="Leasing mjesečno sve uključeno" v={eur(r.lease_monthly_all_in)} />
    <Row l="Kredit: mjesečna rata" v={eur(r.credit_monthly)} /><Row l="Kredit: kamata ukupno" v={eur(r.credit_interest)} />
    <Row l="Kredit ukupno (minus vrijednost auta)" v={eur(r.credit_total)} strong /><Row l="Kredit mjesečno sve uključeno" v={eur(r.credit_monthly_all_in)} />
  </div>
);

function Abf({ onRun, busy }) {
  const [f, set] = useForm({ gross_month: "4000", years: "8", annual_gross: "", tax_class: "1", offered: "" });
  const submit = () => onRun({ gross_month: n(f.gross_month), years: n(f.years), annual_gross: f.annual_gross ? n(f.annual_gross) : null, tax_class: +f.tax_class, offered: f.offered ? n(f.offered) : null });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Mjesečna bruto plata (€)"><Num id="gross-month" v={f.gross_month} set={set("gross_month")} required /></F>
        <F label="Godine staža u firmi"><Num id="years" v={f.years} set={set("years")} /></F>
        <F label="Ostali bruto prihod u godini isplate (€)" hint="Prazno = 12 plata"><Num id="annual" v={f.annual_gross} set={set("annual_gross")} /></F>
        <F label="Porezna klasa"><Sel id="tax-class" v={f.tax_class} set={set("tax_class")} opts={[["1", "I (samac)"], ["3", "III (bračni par, zajedno)"]]} /></F>
        <F label="Ponuđena otpremnina (€)" hint="Opcionalno"><Num id="offered" v={f.offered} set={set("offered")} /></F>
      </div>
    </Form>
  );
}
Abf.Result = ({ r }) => (
  <div data-testid="result-abf">
    <Big l="Regelabfindung (0,5 plate po godini)" v={eur(r.scenarios[0].gross)} id="res-abf-main" />
    <Row l="Uračunate godine" v={r.years_counted} />
    {r.scenarios.map((s) => <Row key={s.factor} l={`${s.label}: bruto / porez / neto`} v={`${eur(s.gross)} / ${eur(s.tax)} / ${eur(s.net)}`} />)}
    {r.offered && <Row l={`Ponuda (faktor ${r.offered.factor ?? "—"}): neto`} v={eur(r.offered.net)} strong id="res-abf-offer" />}
  </div>
);

function Move({ onRun, busy }) {
  const [f, set] = useForm({ new_cold_rent: "800", deposit_months: "3", overlap_months: "1", old_rent: "700", old_deposit_back: "2000" });
  const [items, setItems] = useState([{ label: "Prevoz / kombi", amount: "400", now: true }, { label: "Renoviranje starog stana", amount: "500", now: true }, { label: "Kuhinja i namještaj", amount: "1500", now: false }]);
  const upd = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const submit = () => onRun({ ...Object.fromEntries(Object.entries(f).map(([k, v]) => [k, n(v || 0)])), items: items.filter((x) => x.label).map((x) => ({ ...x, amount: n(x.amount || 0) })) });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Nova neto hladna kirija (€)"><Num id="new-rent" v={f.new_cold_rent} set={set("new_cold_rent")} /></F>
        <F label="Kaucija (broj kirija)"><Num id="deposit-months" v={f.deposit_months} set={set("deposit_months")} /></F>
        <F label="Mjeseci dupla kirija"><Num id="overlap" v={f.overlap_months} set={set("overlap_months")} /></F>
        <F label="Stara kirija mjesečno (€)"><Num id="old-rent" v={f.old_rent} set={set("old_rent")} /></F>
        <F label="Povrat stare kaucije (€)"><Num id="old-deposit" v={f.old_deposit_back} set={set("old_deposit_back")} /></F>
      </div>
      {items.map((x, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`mv-row-${i}`}>
          <input value={x.label} onChange={(e) => upd(i, "label", e.target.value)} className={`${inp} flex-1`} placeholder="Stavka" data-testid={`mv-label-${i}`} />
          <input value={x.amount} onChange={(e) => upd(i, "amount", e.target.value)} className={`${inp} w-28`} placeholder="€" data-testid={`mv-amount-${i}`} />
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={x.now} onChange={(e) => upd(i, "now", e.target.checked)} data-testid={`mv-now-${i}`} /> odmah</label>
          <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Ukloni" data-testid={`mv-del-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>))}
      <button type="button" onClick={() => setItems([...items, { label: "", amount: "", now: true }])} className="mb-2 inline-flex items-center gap-1 text-sm font-semibold" data-testid="mv-add"><Plus className="h-4 w-4" /> Dodaj trošak</button>
    </Form>
  );
}
Move.Result = ({ r }) => (
  <div data-testid="result-move">
    <Big l="Novac potreban na početku" v={eur(r.cash_at_start)} id="res-move-now" />
    <Row l="Kaucija (prva rata)" v={`${eur(r.deposit)} (${eur(r.deposit_first_rate)})`} /><Row l="Dupla kirija" v={eur(r.overlap)} />
    {r.rows.map((x) => <Row key={x.label} l={`${x.label}${x.now ? "" : " (kasnije)"}`} v={eur(x.amount)} />)}
    <Row l="Ukupno" v={eur(r.total)} strong /><Row l="Ukupno nakon povrata stare kaucije" v={eur(r.net_after_old_deposit)} />
  </div>
);

function SelfEmp({ onRun, busy }) {
  const [f, set] = useForm({ revenue_month: "5000", costs_month: "1000", kv_month: "450", rv_month: "300", gewerbe: false, hebesatz: "400", prev_year_revenue: "" });
  const submit = () => onRun({ ...f, revenue_month: n(f.revenue_month), costs_month: n(f.costs_month), kv_month: n(f.kv_month || 0), rv_month: n(f.rv_month || 0), hebesatz: n(f.hebesatz), prev_year_revenue: f.prev_year_revenue ? n(f.prev_year_revenue) : null });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Prihod mjesečno, neto bez PDV-a (€)"><Num id="revenue" v={f.revenue_month} set={set("revenue_month")} required /></F>
        <F label="Poslovni troškovi mjesečno (€)"><Num id="costs" v={f.costs_month} set={set("costs_month")} /></F>
        <F label="Zdravstveno + njega mjesečno (€)"><Num id="kv" v={f.kv_month} set={set("kv_month")} /></F>
        <F label="Penziona štednja / RV mjesečno (€)"><Num id="rv" v={f.rv_month} set={set("rv_month")} /></F>
        <F label="Hebesatz općine (%)" hint="Npr. 400–490 u većim gradovima"><Num id="hebesatz" v={f.hebesatz} set={set("hebesatz")} /></F>
        <F label="Prihod prošle godine (€)" hint="Za Kleinunternehmer"><Num id="prev" v={f.prev_year_revenue} set={set("prev_year_revenue")} /></F>
      </div>
      <Chk id="gewerbe" v={f.gewerbe} set={set("gewerbe")} label="Imam Gewerbe (obrt, trgovina) – nisam Freiberufler" />
    </Form>
  );
}
SelfEmp.Result = ({ r }) => (
  <div data-testid="result-self">
    <Big l="Ostaje mjesečno nakon poreza i osiguranja" v={eur(r.net_month)} id="res-self-net" />
    <Row l="Dobit godišnje" v={eur(r.profit)} /><Row l="Porez na dohodak" v={eur(r.est)} /><Row l="Soli" v={eur(r.soli)} />
    {r.gewst > 0 && <Row l={`Gewerbesteuer (odbitak od ESt ${eur(r.gew_credit)})`} v={eur(r.gewst)} />}
    <Row l="Rezerva za porez mjesečno" v={eur(r.tax_reserve_month)} strong id="res-self-reserve" /><Row l="Porezno opterećenje dobiti" v={`${r.tax_rate} %`} />
    <Row l="Kleinunternehmer moguć" v={r.kleinunternehmer ? "da" : "ne / nepoznato"} />
  </div>
);

const PATHS = [["blue_card", "Plava karta EU (§ 18c Abs. 2)"], ["fachkraft", "Kvalifikovani radnik (§ 18a/18b → § 18c Abs. 1)"], ["general", "Opšte pravilo (§ 9)"], ["spouse_de", "Brak s njemačkim državljaninom (§ 28 Abs. 2)"]];
function Nde({ onRun, busy }) {
  const [f, set] = useForm({ path: "blue_card", since: "2024-01-01", contrib_months: "24", german: "A1", income_secured: true, housing: true });
  const submit = () => onRun({ ...f, contrib_months: +f.contrib_months });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Osnov boravka"><Sel id="path" v={f.path} set={set("path")} opts={PATHS} /></F>
        <F label="Od kada imaš tu dozvolu"><input type="date" value={f.since} onChange={(e) => set("since")(e.target.value)} className={inp} data-testid="in-since" /></F>
        <F label="Mjeseci uplata u penziono (RV)" hint="Iz Rentenversicherungsverlauf"><Num id="contrib" v={f.contrib_months} set={set("contrib_months")} /></F>
        <F label="Nivo njemačkog"><Sel id="german" v={f.german} set={set("german")} opts={[["none", "Nemam certifikat"], ["A1", "A1"], ["A2", "A2"], ["B1", "B1"], ["B2", "B2 ili više"]]} /></F>
      </div>
      <Chk id="income" v={f.income_secured} set={set("income_secured")} label="Živim od svog prihoda (bez Bürgergelda)" />
      <Chk id="housing" v={f.housing} set={set("housing")} label="Imam dovoljno velik stan" />
    </Form>
  );
}
Nde.Result = ({ r }) => (
  <div data-testid="result-nde">
    <Big l={r.all_ok ? "Uslovi po unosima ispunjeni od" : "Najraniji mogući datum (ako ispuniš sve uslove)"} v={fmtD(r.earliest)} id="res-nde-date" />
    {r.checks.map((c) => <Row key={c.label} l={`${c.label}${c.date ? ` – ${fmtD(c.date)}` : ""}`} v={c.ok ? "✓" : "✗"} />)}
  </div>
);

const COUNTRIES = [["BA", "Bosna i Hercegovina"], ["RS", "Srbija"], ["HR", "Hrvatska"], ["ME", "Crna Gora"], ["XK", "Kosovo"], ["MK", "Sjeverna Makedonija"], ["SI", "Slovenija"], ["AL", "Albanija"]];
function Pen({ onRun, busy }) {
  const [f, set] = useForm({ de_months: "240", entgeltpunkte: "" });
  const [rows, setRows] = useState([{ country: "BA", months: "120" }]);
  const submit = () => onRun({ de_months: +f.de_months, entgeltpunkte: f.entgeltpunkte ? n(f.entgeltpunkte) : null, periods: rows.map((x) => ({ country: x.country, months: +x.months || 0 })) });
  return (
    <Form onSubmit={submit} busy={busy}>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Mjeseci osiguranja u Njemačkoj"><Num id="de-months" v={f.de_months} set={set("de_months")} /></F>
        <F label="Penzijski bodovi (Entgeltpunkte)" hint="Iz Renteninformation – opcionalno"><Num id="ep" v={f.entgeltpunkte} set={set("entgeltpunkte")} /></F>
      </div>
      {rows.map((x, i) => (
        <div key={i} className="flex items-center gap-2 rounded-sm border border-border p-2" data-testid={`pen-row-${i}`}>
          <select value={x.country} onChange={(e) => setRows(rows.map((y, j) => (j === i ? { ...y, country: e.target.value } : y)))} className={`${inp} min-w-0 flex-1`} data-testid={`pen-country-${i}`}>{COUNTRIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <input value={x.months} onChange={(e) => setRows(rows.map((y, j) => (j === i ? { ...y, months: e.target.value } : y)))} className={`${inp} w-24 shrink-0`} placeholder="mjeseci" aria-label="Mjeseci" data-testid={`pen-months-${i}`} />
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Ukloni" data-testid={`pen-del-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>))}
      <button type="button" onClick={() => setRows([...rows, { country: "RS", months: "" }])} className="mb-2 inline-flex items-center gap-1 text-sm font-semibold" data-testid="pen-add"><Plus className="h-4 w-4" /> Dodaj državu</button>
    </Form>
  );
}
Pen.Result = ({ r }) => (
  <div data-testid="result-pen">
    <Big l="Ukupni staž za uslove" v={`${r.total_years} godina`} id="res-pen-total" />
    {r.wartezeiten.map((w) => <Row key={w.need} l={w.label} v={w.de_only ? "✓ samo Njemačka" : w.combined ? "✓ uz strane periode" : `✗ fali ${w.missing} mj.`} />)}
    {r.periods.map((p) => <Row key={p.country} l={`${p.country}: ${p.months} mj.`} v={p.agreement} />)}
    {r.de_pension_gross != null && <Row l="Njemačka penzija bruto (bez strane)" v={eur(r.de_pension_gross)} strong id="res-pen-de" />}
  </div>
);

export const NEW_FORMS = { "minijob-midijob": Minijob, arbeitslosengeld: Alg, "odmor-otkaz": Leave, wohngeld: Wohngeld, kinderzuschlag: Kiz, nebenkosten: Nebenkosten, "struja-plin": Energy, "kupovina-stana": Home, "leasing-kredit": Lease,
  abfindung: Abf, selidba: Move, samostalni: SelfEmp, "stalni-boravak": Nde, "penzija-de-balkan": Pen };
